'use client';
// BitcoinMarketDetails Component - Bitcoin prediction market details page
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import CodeIcon from '@mui/icons-material/Code';
import LinkIcon from '@mui/icons-material/Link';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { ComponentErrorBoundary } from '@/components';
import { BitcoinLogo, type BitcoinMarketData } from '@/data/bitcoinMarkets';
import { useMarketPrices } from '@/services/query/markets.queries';
import { CommentsSection } from './MarketDetails/CommentsSection';
import { MarketContext } from './MarketDetails/MarketContext/MarketContext';
import { MarketRules } from './MarketDetails/MarketRules/MarketRules';

interface BitcoinMarketDetailsProps {
  market: BitcoinMarketData;
}

export function BitcoinMarketDetails({ market }: BitcoinMarketDetailsProps) {
  const router = useRouter();
  const [selectedTimeFilter, setSelectedTimeFilter] = useState('12 PM');
  const [tradeAmount, setTradeAmount] = useState('0');
  const [tradeTab, setTradeTab] = useState<'buy' | 'sell'>('buy');
  const [tradeSide, setTradeSide] = useState<'yes' | 'no'>('yes');
  const [selectedOutcome, setSelectedOutcome] = useState(
    market.selectedOutcome || market.outcomes[2].id
  );
  const [timeLeft, setTimeLeft] = useState({ hours: 7, minutes: 7, seconds: 9 });
  const balance = 0; // TODO: Get from user state

  const tokenIds = useMemo(() => {
    try {
      if (typeof market.clobTokenIds === 'string') {
        return JSON.parse(market.clobTokenIds);
      }
      return market.clobTokenIds || [];
    } catch (e) {
      return [];
    }
  }, [market.clobTokenIds]);

  const { data: realtimePrices } = useMarketPrices(tokenIds, true);

  const selectedOutcomeData =
    market.outcomes.find((o) => o.id === selectedOutcome) || market.outcomes[2];

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        let { hours, minutes, seconds } = prev;
        seconds--;
        if (seconds < 0) {
          seconds = 59;
          minutes--;
          if (minutes < 0) {
            minutes = 59;
            hours--;
            if (hours < 0) {
              hours = 0;
            }
          }
        }
        return { hours, minutes, seconds };
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  function handleBack() {
    router.push('/crypto');
  }

  function handleQuickAmount(value: number | string) {
    if (value === 'Max') {
      setTradeAmount(balance.toString());
    } else {
      setTradeAmount((prev) =>
        (
          parseFloat(prev || '0') + (typeof value === 'string' ? parseFloat(value) : value)
        ).toString()
      );
    }
  }

  function handleOutcomeSelect(outcomeId: string) {
    setSelectedOutcome(outcomeId);
  }

  const timeFilters = ['Past', '12 PM', '12 PM Dec 23', '12 PM Dec 24', '12 PM Dec 25', 'More v'];

  return (
    <Box sx={{ py: { xs: 1, sm: 2 } }}>
      {/* Header with Back Button */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={handleBack} size="small" sx={{ color: 'text.secondary' }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ cursor: 'pointer' }}
            onClick={handleBack}
          >
            Back to Crypto
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton size="small" sx={{ color: 'text.secondary', width: 32, height: 32 }}>
            <CodeIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" sx={{ color: 'text.secondary', width: 32, height: 32 }}>
            <BookmarkBorderIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" sx={{ color: 'text.secondary', width: 32, height: 32 }}>
            <LinkIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Sticky Market Banner */}
      <Card
        sx={{
          bgcolor: 'background.paper',
          border: 'none',
          borderRadius: 3,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          position: 'sticky',
          top: 0,
          zIndex: 10,
          mb: 2,
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <BitcoinLogo />
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' } }}
              >
                {market.question}
              </Typography>
            </Box>
            {/* Countdown Timer */}
            <Typography
              variant="body2"
              sx={{
                color: '#ef4444',
                fontWeight: 700,
                fontSize: '0.875rem',
                bgcolor: '#fff',
                px: 2,
                py: 0.5,
                borderRadius: 2,
                whiteSpace: 'nowrap',
              }}
            >
              {String(timeLeft.hours).padStart(2, '0')} HRS{' '}
              {String(timeLeft.minutes).padStart(2, '0')} MINS{' '}
              {String(timeLeft.seconds).padStart(2, '0')} SECS
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 380px', xl: '1fr 420px' },
          gap: { xs: 2, sm: 3 },
          alignItems: 'start',
        }}
      >
        {/* Left Column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Outcomes Table */}
          <Card
            sx={{
              bgcolor: 'background.paper',
              border: 'none',
              borderRadius: 3,
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <CardContent sx={{ p: 0 }}>
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.875rem',
                          borderBottom: '2px solid #e5e7eb',
                          py: 1.5,
                        }}
                      >
                        OUTCOME
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.875rem',
                          borderBottom: '2px solid #e5e7eb',
                          py: 1.5,
                        }}
                      >
                        % CHANCE
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {market.outcomes.map((outcome) => (
                      <TableRow
                        key={outcome.id}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            bgcolor: '#f9fafb',
                          },
                          bgcolor: selectedOutcome === outcome.id ? '#f3f4f6' : 'transparent',
                        }}
                        onClick={() => handleOutcomeSelect(outcome.id)}
                      >
                        <TableCell sx={{ py: 1.5 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography
                              variant="body1"
                              fontWeight={600}
                              sx={{ fontSize: '0.9375rem' }}
                            >
                              {outcome.priceTarget}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: '0.75rem' }}
                            >
                              {outcome.volume} Vol.
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 2,
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography
                                variant="body1"
                                fontWeight={700}
                                sx={{ fontSize: '0.9375rem' }}
                              >
                                {outcome.percentage}
                              </Typography>
                              {outcome.change && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                  <ArrowUpwardIcon sx={{ fontSize: '0.75rem', color: '#22c55e' }} />
                                  <Typography
                                    variant="caption"
                                    sx={{ color: '#22c55e', fontWeight: 600, fontSize: '0.75rem' }}
                                  >
                                    {outcome.change.replace('▲', '')}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Button
                                size="small"
                                variant="contained"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedOutcome(outcome.id);
                                  setTradeSide('yes');
                                }}
                                sx={{
                                  bgcolor:
                                    selectedOutcome === outcome.id && tradeSide === 'yes'
                                      ? '#16a34a'
                                      : '#22c55e',
                                  color: '#fff',
                                  minWidth: 100,
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                  textTransform: 'none',
                                  borderRadius: 2,
                                  px: 1.5,
                                  py: 0.5,
                                  boxShadow: 'none',
                                  '&:hover': {
                                    bgcolor: '#bbf7d0',
                                    boxShadow: 'none',
                                  },
                                }}
                              >
                                Buy Yes{' '}
                                {realtimePrices?.[tokenIds[0]]?.midPrice !== undefined
                                  ? `${(realtimePrices[tokenIds[0]].midPrice * 100).toFixed(1)}¢`
                                  : outcome.yesPrice}
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedOutcome(outcome.id);
                                  setTradeSide('no');
                                }}
                                sx={{
                                  borderColor: '#fecaca',
                                  bgcolor: '#fee2e2',
                                  color: '#991b1b',
                                  minWidth: 100,
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                  textTransform: 'none',
                                  borderRadius: 2,
                                  px: 1.5,
                                  py: 0.5,
                                  boxShadow: 'none',
                                  '&:hover': {
                                    bgcolor: '#fecaca',
                                    borderColor: '#fecaca',
                                    boxShadow: 'none',
                                  },
                                }}
                              >
                                Buy No{' '}
                                {realtimePrices?.[tokenIds[1]]?.midPrice !== undefined
                                  ? `${(realtimePrices[tokenIds[1]].midPrice * 100).toFixed(1)}¢`
                                  : outcome.noPrice}
                              </Button>
                            </Box>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Market Context Section */}
          <ComponentErrorBoundary componentName="Market Context">
            <MarketContext slug={market.slug} />
          </ComponentErrorBoundary>

          {/* Market Rules Section */}
          <ComponentErrorBoundary componentName="Market Rules">
            <MarketRules marketId={market.id} />
          </ComponentErrorBoundary>

          {/* Comments Section */}
          <ComponentErrorBoundary componentName="Comments Section">
            <CommentsSection marketId={market.id} />
          </ComponentErrorBoundary>
        </Box>

        {/* Right Column - Trading Sidebar */}
        <Box
          sx={{
            position: { lg: 'sticky' },
            top: { lg: 120 },
            order: { xs: -1, lg: 0 },
          }}
        >
          <Card
            sx={{
              bgcolor: 'background.paper',
              border: 'none',
              borderRadius: 3,
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <CardContent sx={{ p: 2 }}>
              {/* Market Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <BitcoinLogo />
                <Typography variant="h6" fontWeight={600}>
                  {selectedOutcomeData.priceTarget}
                </Typography>
              </Box>

              {/* Buy/Sell Tabs */}
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button
                  fullWidth
                  variant={tradeTab === 'buy' ? 'contained' : 'outlined'}
                  onClick={() => setTradeTab('buy')}
                  sx={{
                    bgcolor: tradeTab === 'buy' ? '#2563eb' : 'transparent',
                    color: tradeTab === 'buy' ? '#fff' : 'text.secondary',
                    borderColor: 'divider',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: tradeTab === 'buy' ? '#1d4ed8' : 'action.hover',
                    },
                  }}
                >
                  Buy
                </Button>
                <Button
                  fullWidth
                  variant={tradeTab === 'sell' ? 'contained' : 'outlined'}
                  onClick={() => setTradeTab('sell')}
                  sx={{
                    bgcolor: tradeTab === 'sell' ? '#2563eb' : 'transparent',
                    color: tradeTab === 'sell' ? '#fff' : 'text.secondary',
                    borderColor: 'divider',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: tradeTab === 'sell' ? '#1d4ed8' : 'action.hover',
                    },
                  }}
                >
                  Sell
                </Button>
              </Box>

              {/* Market Dropdown */}
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <Select
                  value="market"
                  sx={{
                    borderRadius: 2,
                    fontSize: '0.875rem',
                  }}
                >
                  <MenuItem value="market">Market</MenuItem>
                </Select>
              </FormControl>

              {/* Yes/No Buttons */}
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button
                  fullWidth
                  variant={tradeSide === 'yes' ? 'contained' : 'outlined'}
                  onClick={() => setTradeSide('yes')}
                  sx={{
                    bgcolor: tradeSide === 'yes' ? '#dcfce7' : 'transparent',
                    color: tradeSide === 'yes' ? '#166534' : 'text.secondary',
                    borderColor: 'divider',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: tradeSide === 'yes' ? '#bbf7d0' : 'action.hover',
                    },
                  }}
                >
                  Yes{' '}
                  {realtimePrices?.[tokenIds[0]]?.midPrice !== undefined
                    ? `${(realtimePrices[tokenIds[0]].midPrice * 100).toFixed(1)}¢`
                    : selectedOutcomeData.yesPrice}
                </Button>
                <Button
                  fullWidth
                  variant={tradeSide === 'no' ? 'contained' : 'outlined'}
                  onClick={() => setTradeSide('no')}
                  sx={{
                    bgcolor: tradeSide === 'no' ? '#fee2e2' : 'transparent',
                    color: tradeSide === 'no' ? '#991b1b' : 'text.secondary',
                    borderColor: 'divider',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: tradeSide === 'no' ? '#fecaca' : 'action.hover',
                    },
                  }}
                >
                  No{' '}
                  {realtimePrices?.[tokenIds[1]]?.midPrice !== undefined
                    ? `${(realtimePrices[tokenIds[1]].midPrice * 100).toFixed(1)}¢`
                    : selectedOutcomeData.noPrice}
                </Button>
              </Box>

              {/* Amount Section */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                  Amount
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 1, display: 'block' }}
                >
                  Balance ${balance.toFixed(2)}
                </Typography>
                <TextField
                  fullWidth
                  value={tradeAmount}
                  onChange={(e) => setTradeAmount(e.target.value)}
                  placeholder="$0"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  sx={{
                    mb: 1,
                    '& .MuiOutlinedInput-root': {
                      fontSize: '1.5rem',
                      fontWeight: 600,
                      borderRadius: 2,
                    },
                  }}
                />
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleQuickAmount(1)}
                    sx={{
                      flex: 1,
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      borderColor: 'divider',
                      color: 'text.secondary',
                    }}
                  >
                    +$1
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleQuickAmount(20)}
                    sx={{
                      flex: 1,
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      borderColor: 'divider',
                      color: 'text.secondary',
                    }}
                  >
                    +$20
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleQuickAmount(100)}
                    sx={{
                      flex: 1,
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      borderColor: 'divider',
                      color: 'text.secondary',
                    }}
                  >
                    +$100
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleQuickAmount('Max')}
                    sx={{
                      flex: 1,
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      borderColor: 'divider',
                      color: 'text.secondary',
                    }}
                  >
                    Max
                  </Button>
                </Box>
              </Box>

              {/* Deposit Button */}
              <Button
                fullWidth
                variant="contained"
                sx={{
                  bgcolor: '#2563eb',
                  color: '#fff',
                  py: 1.5,
                  mb: 2,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: '#1d4ed8',
                  },
                }}
              >
                Deposit
              </Button>

              {/* Terms of Use */}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.75rem', display: 'block', textAlign: 'center' }}
              >
                By trading, you agree to the{' '}
                <Link href="#" sx={{ color: '#2563eb', textDecoration: 'none' }}>
                  Terms of Use
                </Link>
                .
              </Typography>
            </CardContent>
          </Card>

          {/* Related Markets */}
          <Card
            sx={{
              bgcolor: 'background.paper',
              border: 'none',
              borderRadius: 3,
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              mt: 2,
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Related Markets
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <BitcoinLogo />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={500}>
                      Bitcoin all time high by December 31, 2026?
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={600} sx={{ color: '#22c55e' }}>
                    47%
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      bgcolor: '#fbbf24',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#fff' }}>
                      BNB
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={500}>
                      BNB all time high by December 31?
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={600} sx={{ color: '#22c55e' }}>
                    1%
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Trade Modal - Removed dead code */}
    </Box>
  );
}

export default BitcoinMarketDetails;
